import {bootstrapApplication} from '@angular/platform-browser';
import {provideRouter, withHashLocation} from '@angular/router';
import {AppComponent} from './app/app.component';
import {routes} from './app/app.routes';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {delegationContextInterceptor} from './app/core/interceptors/delegation-context.interceptor';
import {loaderInterceptor} from './app/core/interceptors/loader.interceptor';
import {sessionRefreshInterceptor} from './app/core/interceptors/session-refresh.interceptor';
import {csrfInterceptor} from './app/core/interceptors/csrf.interceptor';
import {APP_INITIALIZER, importProvidersFrom, ɵresolveComponentResources as resolveComponentResources} from '@angular/core';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {UiToastService} from 'freyr-ui';
import {BuildVersionService} from './app/core/services/build-version.service';
import { environment } from './environments/environment';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

function initializeBuildVersion(buildVersionService: BuildVersionService) {
  return () => buildVersionService.initialize();
}

/**
 * Liens historiques ou externes du type /dashboard?adminPreviewUserId=… (sans #) :
 * avec HashLocationStrategy, le routeur ne reçoit pas ces paramètres. On redirige vers #/dashboard?…
 */
function redirectLegacyDashboardAdminPreviewToHashUrl(): void {
  if (typeof window === 'undefined' || !window.location) {
    return;
  }
  const url = new URL(window.location.href);
  const previewId = url.searchParams.get('adminPreviewUserId');
  if (!previewId?.trim()) {
    return;
  }
  const path = (url.pathname || '/').replace(/\/$/, '') || '/';
  if (!path.endsWith('/dashboard')) {
    return;
  }
  const hash = url.hash || '';
  if (hash.includes('adminPreviewUserId')) {
    return;
  }
  window.location.replace(
    `${url.origin}/#/dashboard?adminPreviewUserId=${encodeURIComponent(previewId.trim())}`
  );
}

function redirectLegacyPublicPanelToHashUrl(): void {
  if (typeof window === 'undefined' || !window.location) {
    return;
  }
  const url = new URL(window.location.href);
  const hash = url.hash || '';
  if (hash && hash !== '#/' && hash !== '#') {
    return;
  }
  const path = (url.pathname || '').replace(/\/+$/, '');
  const match = path.match(/\/panel\/(streamer|levels)\/([^/]+)$/);
  if (!match) {
    return;
  }
  const section = match[1];
  const identifier = decodeURIComponent(match[2] || '').trim();
  if (!identifier) {
    return;
  }
  window.location.replace(`${url.origin}/#/panel/${section}/${encodeURIComponent(identifier)}`);
}

function appInitializerRedirectLegacyAdminPreviewUrl(): () => void {
  return () => {
    redirectLegacyDashboardAdminPreviewToHashUrl();
    redirectLegacyPublicPanelToHashUrl();
  };
}

async function bootstrapFreyr(): Promise<void> {
  // Angular DevTools may request JIT metadata in local development.
  if (!environment.production) {
    await import('@angular/compiler');
    await resolveComponentResources(async () => '');
  }

  await bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(routes, withHashLocation()),
      provideHttpClient(withInterceptors([sessionRefreshInterceptor, csrfInterceptor, delegationContextInterceptor, loaderInterceptor])),
      UiToastService,
      {
        provide: APP_INITIALIZER,
        useFactory: appInitializerRedirectLegacyAdminPreviewUrl,
        multi: true
      },
      {
        provide: APP_INITIALIZER,
        useFactory: initializeBuildVersion,
        deps: [BuildVersionService],
        multi: true
      },
      importProvidersFrom(
        TranslateModule.forRoot({
          defaultLanguage: 'fr',
          loader: {
            provide: TranslateLoader,
            useFactory: createTranslateLoader,
            deps: [HttpClient]
          }
        })
      )
    ]
  });
}

bootstrapFreyr().catch(err => console.error(err));

